import React, { useContext, useCallback, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type MutationFunctionInteropSupportedOptions } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { isOptimisticSaveEnabledForTypeAndMode } from '@confluence/comment/entry-points/draftUtils.ts';
import { PageCommentEditor } from '@confluence/comment-simple/entry-points/PageCommentEditor.ts';
import type { CommentSaveHandler } from '@confluence/comment-simple/entry-points/commentSimpleTypes.ts';
import {
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { startEditorSubExperience } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { CommentData } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	ADF_COMMENT_CONTENT_REPRESENTATION,
	WEB_COMMENT_SOURCE,
	addEmptyObjectRepliesAndReactions,
	getOptimisticResponseForObjectComment,
	useCreateObjectCommentMutation,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import type {
	useCreateObjectCommentMutation$data,
	useCreateObjectCommentMutation$variables,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutationTypes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import {
	useCommentsPanelActions,
	AddFooterCommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import {
	getTranslatedError,
	isCommentAbortableError,
} from '@confluence/comments-util/entry-points/objectCommentsErrorUtils.ts';
import {
	handleFooterCommentsNavigation,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { objectCommentsi18n } from '@confluence/comments-util/entry-points/objectCommentsi18n.ts';
import { useAgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/useAgentMentionFyiNudge.ts';
import { AgentMentionFyiNudgeAsync as AgentMentionFyiNudge } from '@confluence/agent-mention/entry-points/AgentMentionFyiNudgeAsync.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper.tsx';

export interface ObjectAddCommentEditorProps {
	isOffline?: boolean;
	setAutoFocusPromptRef?: (value: boolean) => void;
	handleCancel?: () => void;
}

export const ObjectAddCommentEditor: React.ComponentType<
	Omit<ObjectAddCommentEditorProps, 'flags'>
> = withFlags(
	({
		flags,
		isOffline,
		setAutoFocusPromptRef,
		handleCancel,
	}: WithFlagsProps & ObjectAddCommentEditorProps): React.JSX.Element => {
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { closeCommentContainer } = useObjectCommentDispatchContext();
		const { footerEditorEntry } = useObjectCommentContext();
		const [contentId] = usePageContentId();
		const [{ searchSessionId }] = useSearchSessionId();

		const { setCurrentlySelectedThreadKey, setAddFooterCommentAdf } = useCommentsPanelActions();
		const { hasContentChanged } = useCommentsContentState();
		const { onChange, resetContentChanged } = useCommentsContentActions();
		const { cloudId, userId, displayName } = useSessionData();
		const {
			showFyi,
			agent,
			onContentChange: onAgentMentionContentChange,
		} = useAgentMentionFyiNudge({
			cloudId,
			enabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
		});
		const hasShownFyiRef = useRef(false);
		useEffect(() => {
			if (showFyi) {
				hasShownFyiRef.current = true;
			}
		}, [showFyi]);
		const pageMode = useGetPageMode();
		const [contentType] = useContentType();
		const renderServerPlaceholder = useIsSSROrHydrating();

		// Ref to store editor actions for focusing when sticky draft is clicked
		const editorActionsRef = useRef<any>(null);

		// Focus editor when opened from sticky draft (handles case when editor is already mounted)
		useEffect(() => {
			if (
				footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT &&
				editorActionsRef.current?.focus
			) {
				editorActionsRef.current.focus();
			}
		}, [footerEditorEntry]);

		const { addCommentInCache } = useAllCommentsData({
			contentId: contentId || '',
			options: {
				skip: true, // Only using write operations, no need to fetch data
			},
		});
		const createObjectCommentMutationFn = useCreateObjectCommentMutation();

		const clearAddFooterCommentAdf = useCallback(() => {
			setAddFooterCommentAdf(undefined);
		}, [setAddFooterCommentAdf]);

		const failPublishExperience = useCallback(
			(error: Error) => {
				// Mark the experience as failed/aborted and include the error details
				experienceTracker.stopOnError({
					name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
					error,
				});
			},
			[experienceTracker],
		);

		const handleCacheMiss = useCallback(
			(_error: Error) => {
				void flags?.showWarningFlag({
					title: <FormattedMessage {...objectCommentsi18n.unableToLoadCommentsTitle} />,
					description: <FormattedMessage {...objectCommentsi18n.networkError} />,
				});
			},
			[flags],
		);

		const updateNewCommentInCache: NonNullable<
			MutationFunctionInteropSupportedOptions<
				useCreateObjectCommentMutation$data,
				useCreateObjectCommentMutation$variables
			>['update']
		> = useCallback(
			(_dataProxy, mutationResult) => {
				addCommentInCache({
					newComment: addEmptyObjectRepliesAndReactions(mutationResult.data!.createFooterComment),
					onError: failPublishExperience,
					onCacheMiss: handleCacheMiss,
				});
			},
			[addCommentInCache, failPublishExperience, handleCacheMiss],
		);

		const handleCommentsPanelSave: CommentSaveHandler = (adfObject: any, onSuccess: any) => {
			const pageId = contentId || '';
			const { adf } = adfObject;

			startEditorSubExperience({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				timeout: ExperienceTimeout.EDITOR_PUBLISH,
			});

			const mutationVariables = {
				cloudId,
				input: {
					commentBody: {
						value: JSON.stringify(adf),
						representationFormat: ADF_COMMENT_CONTENT_REPRESENTATION,
					},
					commentSource: WEB_COMMENT_SOURCE,
					containerId: pageId,
				},
			};

			const isOptimisticAddEnabled = isOptimisticSaveEnabledForTypeAndMode('page', 'create');
			const optimisticResponse = isOptimisticAddEnabled
				? getOptimisticResponseForObjectComment({
						userId: userId ?? '',
						displayName: displayName ?? '',
						adf,
						pageId,
						parentCommentId: null,
					})
				: undefined;
			const optimisticCommentId = optimisticResponse?.createFooterComment?.id;
			if (isOptimisticAddEnabled) {
				onSuccess && onSuccess();
				clearAddFooterCommentAdf();
				closeCommentContainer();
				// set the new comment as selected and scroll to it
				optimisticCommentId &&
					setCurrentlySelectedThreadKey(optimisticCommentId, CommentType.GENERAL);
				optimisticCommentId &&
					handleFooterCommentsNavigation({
						scrollBehavior: ScrollBehavior.SMOOTH,
						block: 'center',
						inline: 'center',
						commentId: optimisticCommentId,
					});
			}

			const mutationPromise = createObjectCommentMutationFn({
				variables: {
					...mutationVariables,
					pageId,
					includeReactionsSummary: !expValEquals(
						'confluence_fe_use_create_comment_exclude_reactions',
						'isEnabled',
						true,
					),
				},
				// @ts-ignore -- can't import type directly in update FN because it'd be circular
				update: updateNewCommentInCache,
				optimisticResponse,
			});

			return mutationPromise
				.then(({ data }: any) => {
					const commentResult = data.createFooterComment;
					const comment: CommentData = {
						...commentResult,
						replies: [], // not included in the payload
						isOpen: true,
						type: CommentType.GENERAL,
					};

					if (!isOptimisticAddEnabled) {
						onSuccess && onSuccess(); // This is CommentEditor's `resetEditor` callback.
						clearAddFooterCommentAdf();
						closeCommentContainer();
					}
					resetContentChanged();

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'created',
							actionSubject: 'comment',
							actionSubjectId: comment.id, // The newly created comment ID
							objectType: contentType,
							objectId: contentId,
							source: AnalyticsSource.PAGE_FOOTER,
							attributes: {
								commentType: AnalyticsCommentType.PAGE,
								source: AnalyticsSource.PAGE_FOOTER,
								pageMode,
								isReply: false,
								pageType: contentType,
								isLivePage: pageMode === PageMode.LIVE,
								editor: 'v2',
								searchSessionId,
								navdexPointType: END,
								parentCommentId: null, // analytics event schema type expects string or null
								isStickyFooter: footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT, // Keeps track of where the actual create came from
							},
						},
					}).fire();

					experienceTracker.succeed({
						name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
					});

					if (!isOptimisticAddEnabled) {
						// set the new comment as selected and scroll to it - already done if optimistic add is enabled
						setCurrentlySelectedThreadKey(comment.id, CommentType.GENERAL);
						void handleFooterCommentsNavigation({
							scrollBehavior: ScrollBehavior.SMOOTH,
							block: 'center',
							inline: 'center',
							commentId: comment.id,
						});
					} else {
						// set the new comment as selected with its actual id
						setCurrentlySelectedThreadKey(comment.id, CommentType.GENERAL, true);
					}
					// Return the new comment ID so downstream consumers (e.g. PageCommentEditor)
					// can pass it to handleAgentMentionInComment without needing to re-fetch it.
					return { id: comment.id };
				})
				.catch((error: Error) => {
					const translatedError = getTranslatedError(error);
					const isAbortableError = isCommentAbortableError(translatedError);
					if (isAbortableError) {
						// TODO: show an error message to the user for create not permitted
						experienceTracker.abort({
							name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
							reason: translatedError.id,
						});
						// Abort parent experience too, since it's not automatically aborted when child is aborted.
						experienceTracker.abort({
							name: ADD_PAGE_COMMENT_EXPERIENCE,
							reason: translatedError.id,
						});
					} else {
						failPublishExperience(error);
					}
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					if (fg('confluence_comments_error_handling_improvements')) {
						return Promise.reject({ error: translatedError });
					}
				});
		};

		const handleCancelInternal = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
			const doCancel = () => {
				if (isEditorAborted) {
					setAutoFocusPromptRef?.(true);
					experienceTracker.abort({
						name: ADD_PAGE_COMMENT_EXPERIENCE,
						reason: 'aborted because comment was cancelled',
					});
				}

				clearAddFooterCommentAdf();
				closeCommentContainer();
				handleCancel?.();
			};

			if (hasContentChanged) {
				resetContentChanged();
			}
			doCancel();
		};

		const handleOnEditorLoaded = () => {
			experienceTracker.succeed({
				name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
			});

			const footerCommentContainer =
				renderServerPlaceholder || typeof document === 'undefined'
					? null
					: document.querySelector('[data-testId="footerCommentEditor"]');

			if (!footerCommentContainer) {
				return;
			}

			footerCommentContainer.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest',
			});
		};

		const handleEditorReady = (editorActions: any) => {
			handleOnEditorLoaded();
			// Store editor actions for later use (e.g., focusing from sticky draft)
			editorActionsRef.current = editorActions;
			// Focus the editor when opened from sticky draft
			if (footerEditorEntry === AddFooterCommentMode.STICKY_DRAFT && editorActions?.focus) {
				editorActions.focus();
			}
		};

		// This is debounced at the editor level (at 150ms) to prevent constant updates hurting performance
		const handleEditorContentChangedWithData: NonNullable<
			React.ComponentProps<typeof PageCommentEditor>['onEditorContentChangedWithData']
		> = (hasContent, content) => {
			// Store the actual ADF content for real-time sync
			if (hasContent && content) {
				setAddFooterCommentAdf(content);
			} else {
				setAddFooterCommentAdf(undefined);
			}

			// Also call the original onChange handler
			onChange(hasContent);
			onAgentMentionContentChange(content);
		};

		const agentMentionFyiNudge =
			(showFyi || hasShownFyiRef.current) &&
			expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true) ? (
				<AgentMentionFyiNudge visible={showFyi} agent={agent} />
			) : null;

		return (
			<ObjectAddCommentProfileWrapper banner={agentMentionFyiNudge}>
				<PageCommentEditor
					mode="create"
					saveHandler={handleCommentsPanelSave}
					cancelHandler={handleCancelInternal}
					onEditorLoaded={handleOnEditorLoaded}
					onEditorReady={handleEditorReady}
					onEditorContentChangedWithData={handleEditorContentChangedWithData}
					useNewExperienceAbort
					expandEditor
					isFooterComment
					isOffline={isOffline}
					showAgentMentionFyiNudge={false}
				/>
			</ObjectAddCommentProfileWrapper>
		);
	},
);
